import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('adminToken');
      sessionStorage.removeItem('adminInfo');
      if (window.location.pathname.startsWith('/ascorpadmin') && window.location.pathname !== '/ascorpadmin/entry') {
        window.location.href = '/ascorpadmin/entry';
      }
    }
    return Promise.reject(error);
  }
);

// Application API
export const applicationsApi = {
  create: (data) => api.post('/applications', data),
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.productType) params.append('productType', filters.productType);
    if (filters.inquiryType) params.append('inquiryType', filters.inquiryType);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.page) params.append('page', filters.page);
    if (filters.pageSize) params.append('pageSize', filters.pageSize);
    const queryString = params.toString();
    return api.get(`/applications${queryString ? '?' + queryString : ''}`);
  },
  updateStatus: (id, status) => api.patch(`/applications/${id}/status`, { status }),
  delete: (id) => api.delete(`/applications/${id}`),
  bulkDelete: (ids) => api.post('/applications/bulk-delete', { ids }),
  bulkUpdateStatus: (ids, status) => api.post('/applications/bulk-status', { ids, status }),
  exportExcel: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.productType) params.append('productType', filters.productType);
    if (filters.inquiryType) params.append('inquiryType', filters.inquiryType);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    const queryString = params.toString();
    return api.get(`/applications/export${queryString ? '?' + queryString : ''}`, {
      responseType: 'blob',
    });
  },
};

// Admin API
export const adminApi = {
  login: (email, password) => api.post('/admin/login', { email, password }),
  logout: () => api.post('/admin/logout'),
  verify: () => api.get('/admin/verify'),
  changePassword: (currentPassword, newPassword) => api.post('/admin/change-password', { currentPassword, newPassword }),
  listUsers: () => api.get('/admin/users'),
  createUser: (name, email, password) => api.post('/admin/users', { name, email, password }),
  deleteUser: (email) => api.delete(`/admin/users/${encodeURIComponent(email)}`),
};

// Auth helpers (sessionStorage: clears on tab close, reduces XSS token exposure window)
export const auth = {
  setToken: (token) => sessionStorage.setItem('adminToken', token),
  getToken: () => sessionStorage.getItem('adminToken'),
  removeToken: () => {
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminInfo');
  },
  setAdminInfo: (info) => sessionStorage.setItem('adminInfo', JSON.stringify(info)),
  getAdminInfo: () => {
    const info = sessionStorage.getItem('adminInfo');
    return info ? JSON.parse(info) : null;
  },
  isAuthenticated: () => !!sessionStorage.getItem('adminToken'),
};

export default api;
